"use client";

import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function PageHeader() {
  const {
    ref: indexRef,
    isVisible: indexVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: headlineRef,
    isVisible: headlineVisible
  } = useRevealOnScroll<HTMLHeadingElement>({
    threshold: 0.15
  });
  const {
    ref: bodyRef,
    isVisible: bodyVisible
  } = useRevealOnScroll<HTMLParagraphElement>({
    threshold: 0.15
  });
  const {
    ref: ctaRef,
    isVisible: ctaVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: imageRef,
    isVisible: imageVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <section data-section-id="1607" id="page-header" className="relative min-h-screen w-full bg-background text-foreground pt-32 pb-24 border-t border-b border-border overflow-hidden">
      <div className="mx-auto max-w-[1440px] px-6 md:px-16 lg:px-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-8 min-h-[calc(100vh-14rem)]">
          {}
          <div ref={imageRef} className={`lg:col-span-7 flex flex-col transition-all duration-700 ease-out ${imageVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
            {}
            <div className="relative w-full mb-4">
              <div className={`h-px bg-border transition-all duration-500 ease-out ${imageVisible ? "w-full" : "w-0"}`} />
              <span className="absolute right-0 top-1/2 -translate-y-1/2 w-1.5 h-1.5 bg-[var(--chart-1)]" />
            </div>

            {}
            <div className="mb-6 flex items-center justify-between">
              <span className="font-mono text-[0.6875rem] tracking-[0.18em] uppercase text-muted-foreground">
                Series 01 — Industrial Precision
              </span>
              <span className="font-mono text-[0.6875rem] tracking-[0.18em] uppercase text-muted-foreground hidden md:inline">
                Laser Engraving, Metal Finish
              </span>
            </div>

            {}
            <div className="relative flex-1 overflow-hidden">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/industrial_engraving_precision.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/industrial_engraving_precision.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/industrial_engraving_precision.png-wvc-sizes" alt="High-precision industrial laser engraving on stainless steel surface" loading="lazy" decoding="async" className={`h-full w-full object-cover min-h-[420px] md:min-h-[560px] lg:min-h-[640px] transition-opacity duration-700 ease-out ${imageVisible ? "opacity-100" : "opacity-0"}`} />
            </div>
          </div>

          {}
          <div className="lg:col-span-5 flex flex-col justify-center lg:pl-8">
            {}
            <div ref={indexRef} className={`mb-16 md:mb-20 transition-all duration-700 ease-out ${indexVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
              <span className="font-mono text-[0.6875rem] md:text-[0.8125rem] tracking-[0.18em] uppercase text-foreground">
                N° 01 — Industrial Marking
              </span>
            </div>

            {}
            <h1 ref={headlineRef} className="font-default font-extralight text-foreground" style={{
            fontSize: "clamp(3.25rem, 7.2vw, 7.5rem)",
            letterSpacing: "-0.03em",
            lineHeight: "0.95",
            transitionDelay: "80ms"
          }}>
              <span className={`inline-block transition-all duration-700 ease-out ${headlineVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
                Engineered marking for{" "}
                <em className={`not-italic`} style={{
                fontStyle: "italic",
                fontWeight: 200,
                color: headlineVisible ? "var(--chart-1)" : "var(--foreground)",
                transition: "color 600ms ease-out 400ms"
              }}>
                  critical
                </em>{" "}
                infrastructure.
              </span>
            </h1>

            {}
            <p ref={bodyRef} className={`mt-14 max-w-md font-default font-light text-muted-foreground transition-all duration-700 ease-out ${bodyVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
            fontSize: "clamp(1.0625rem, 1.15vw, 1.25rem)",
            letterSpacing: "-0.005em",
            lineHeight: "1.55",
            transitionDelay: "160ms"
          }}>
              Professional-grade engraving and labeling solutions built for durability, compliance, and long-term performance in demanding environments.
            </p>

            {}
            <div ref={ctaRef} className={`mt-12 flex flex-col items-start gap-6 transition-all duration-700 ease-out ${ctaVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
            transitionDelay: "240ms"
          }}>
              <Button asChild className="group relative bg-primary px-9 py-5 h-auto text-primary-foreground hover:bg-[var(--chart-1)] transition-colors duration-[220ms] font-default font-light text-base tracking-[0.04em]">
                <Link to="/ContactPage">
                  <span className="relative inline-block">
                    Request a quote
                    <span className="absolute left-0 -bottom-1 h-px w-0 bg-accent transition-all duration-[320ms] ease-out group-hover:w-full" />
                  </span>
                </Link>
              </Button>

              <Link to="/ProductsPage" className="group font-default text-foreground font-light text-base" style={{
              letterSpacing: "0"
            }}>
                <span className="inline-block border-b border-border pb-1 transition-all duration-[220ms] ease-out group-hover:border-[var(--chart-1)]" style={{
                textUnderlineOffset: "6px"
              }}>
                  Explore our products
                </span>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>;
}